import { useId } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowUp, ChevronDown } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { orderingDir, orderingField, type Ordering, type SortDir } from "@/shared/lib/ordering";

export interface SortOption<F extends string> {
  field: F;
  label: string;
  /** Direction applied when this field is picked (same meaning as SortHeader's firstDir). */
  firstDir?: SortDir;
}

interface MobileSortProps<F extends string> {
  options: SortOption<F>[];
  /** Current ordering ("field" / "-field"), or null when the list uses its default order. */
  ordering: string | null;
  onSortChange: (next: Ordering<F>) => void;
  className?: string;
}

// Phone stand-in for the clickable column headers (the table collapses to cards there):
// a native field picker + an asc/desc toggle. Same ordering strings as SortHeader.
export function MobileSort<F extends string>({ options, ordering, onSortChange, className }: MobileSortProps<F>) {
  const { t } = useTranslation();
  const selectId = useId();
  const field = orderingField(ordering) as F | null;
  const dir = orderingDir(ordering);
  const known = options.some((o) => o.field === field);

  function apply(next: F, nextDir: SortDir) {
    onSortChange(nextDir === "desc" ? `-${next}` : next);
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <label htmlFor={selectId} className="sr-only">
        {t("table.sortBy")}
      </label>
      <div className="relative min-w-0 flex-1">
        <select
          id={selectId}
          value={known ? (field as string) : ""}
          onChange={(e) => {
            const opt = options.find((o) => o.field === e.target.value);
            if (opt) apply(opt.field, opt.firstDir ?? "desc");
          }}
          className="field-focus h-10 w-full appearance-none truncate rounded-control border border-grey-300 bg-white pl-3 pr-9 text-[13.5px] font-medium text-grey-800"
        >
          {!known && <option value="">{t("table.sortDefault")}</option>}
          {options.map((o) => (
            <option key={o.field} value={o.field}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-grey-400"
        />
      </div>
      <button
        type="button"
        disabled={!known}
        onClick={() => field && apply(field, dir === "asc" ? "desc" : "asc")}
        aria-label={dir === "asc" ? t("table.sortAsc") : t("table.sortDesc")}
        title={dir === "asc" ? t("table.sortAsc") : t("table.sortDesc")}
        className="flex h-10 w-10 flex-none items-center justify-center rounded-control border border-grey-300 bg-white text-grey-700 transition-transform active:scale-[0.96] disabled:opacity-40"
      >
        {dir === "asc" ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
      </button>
    </div>
  );
}
